import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Sidebar from "../../sidebar";
import { pageMotion } from "../../motion/pageMotion";
import { guardianAlertService } from "../../../services/guardianAlertService";
import {
  getAlertSearchErrorMessage,
  isValidAlertCode,
  normalizeAlertCode,
} from "../../../utils/guardianAlertByCode";
import {
  MainContainer,
  SideContainer,
  PageContent,
} from "../ViewAlerts/styles";
import { BackgroundPattern } from "../ListAreas/styles";
import {
  AlertCard,
  CardHead,
  CardList,
  CodeBadge,
  CodesField,
  ErrorText,
  FieldLabel,
  GhostButton,
  Hint,
  MetaLine,
  NoticeCard,
  NoticeText,
  PageTitle,
  PrimaryButton,
  ResponseBlock,
  ResponseLabel,
  ResponseValue,
  RowActions,
  StatusLine,
  TextArea,
  Toolbar,
} from "./styles";

const NOTICE =
  "Olá, usuário com privilégios pode responder vários alertas de uma vez. Tenha atenção aos procedimentos executados.";

const FINAL_ACTION = "respondido_processo_sucessao";

function parseCodes(raw) {
  const parts = String(raw || "")
    .split(/[\s,;]+/)
    .map((part) => normalizeAlertCode(part))
    .filter(Boolean);
  const valid = [];
  const invalid = [];
  const seen = new Set();
  parts.forEach((code) => {
    if (!isValidAlertCode(code)) {
      invalid.push(code);
      return;
    }
    if (seen.has(code)) return;
    seen.add(code);
    valid.push(code);
  });
  return { valid, invalid };
}

function ticketErrorMessage(err) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.error) return String(data.error);
  if (data?.detail) return String(data.detail);
  if (data && typeof data === "object") {
    const first = Object.values(data)[0];
    if (Array.isArray(first) && first[0]) return String(first[0]);
    if (typeof first === "string") return first;
  }
  return err?.message || "Não foi possível registrar a resposta.";
}

function displayText(value) {
  const text = String(value || "").trim();
  if (!text || text.toLowerCase() === "nenhuma") return "Sem registro";
  return text;
}

export default function BulkAlertResponse() {
  const navigate = useNavigate();
  const collapsed = useSelector((state) => state.sidebar);
  const [phase, setPhase] = useState("notice");
  const [codesText, setCodesText] = useState("");
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const goBack = () => navigate("/guardian");

  const updateItem = (code, patch) => {
    setItems((current) =>
      current.map((item) =>
        item.code === code
          ? {
              ...item,
              ...patch,
              status: patch.status || (item.status === "saved" ? "idle" : item.status),
            }
          : item
      )
    );
  };

  const loadAlerts = async () => {
    const { valid, invalid } = parseCodes(codesText);
    if (!valid.length) {
      setLoadError(
        invalid.length
          ? "Nenhum código válido. Use o código de 5 caracteres do alerta."
          : "Informe ao menos um código de alerta."
      );
      return;
    }

    setLoading(true);
    setLoadError(
      invalid.length
        ? `Códigos ignorados: ${invalid.join(", ")}.`
        : ""
    );

    const loaded = await Promise.all(
      valid.map(async (code) => {
        try {
          const data = await guardianAlertService.getAlertByCode(code);
          if (!data?.alert?.id) {
            return {
              code,
              error: "Este código é de uma solicitação, não de um alerta criado.",
            };
          }
          return {
            code: data.alert.cod_alerta || code,
            alert: data.alert,
            responses: "",
            observations: "",
            status: "idle",
            message: "",
          };
        } catch (err) {
          return { code, error: getAlertSearchErrorMessage(err) };
        }
      })
    );

    setItems((current) => {
      const byCode = new Map(current.filter((item) => item.alert).map((item) => [item.code, item]));
      loaded.forEach((item) => {
        if (!byCode.has(item.code)) byCode.set(item.code, item);
      });
      return Array.from(byCode.values());
    });
    setLoading(false);
  };

  const submitAll = async () => {
    const pending = items.filter((item) => item.alert && item.status !== "saved");
    if (!pending.length) {
      setLoadError("Carregue alertas e escreva a resposta de cada um.");
      return;
    }

    const missing = pending.filter((item) => !item.responses.trim());
    if (missing.length) {
      setItems((current) =>
        current.map((item) =>
          missing.some((row) => row.code === item.code)
            ? { ...item, status: "error", message: "Escreva a resposta deste alerta." }
            : item
        )
      );
      return;
    }

    setSubmitting(true);
    setLoadError("");
    await Promise.all(
      pending.map(async (item) => {
        try {
          await guardianAlertService.createFieldAlertTicket({
            alertId: item.alert.id,
            action: FINAL_ACTION,
            responses: item.responses.trim(),
            observations: item.observations.trim(),
          });
          updateItem(item.code, {
            status: "saved",
            message: "Resposta registrada.",
          });
        } catch (err) {
          updateItem(item.code, {
            status: "error",
            message: ticketErrorMessage(err),
          });
        }
      })
    );
    setSubmitting(false);
  };

  const readyCount = items.filter((item) => item.alert).length;

  return (
    <MainContainer>
      <BackgroundPattern />
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <PageContent>
          <AnimatePresence mode="wait">
            {phase === "notice" ? (
              <motion.div
                key="bulk-notice"
                initial={pageMotion.initial}
                animate={pageMotion.animate}
                exit={pageMotion.exit}
                transition={pageMotion.transition}
              >
                <NoticeCard>
                  <NoticeText>{NOTICE}</NoticeText>
                  <RowActions style={{ justifyContent: "center" }}>
                    <GhostButton type="button" onClick={goBack}>
                      Voltar
                    </GhostButton>
                    <PrimaryButton type="button" onClick={() => setPhase("form")}>
                      Continuar
                    </PrimaryButton>
                  </RowActions>
                </NoticeCard>
              </motion.div>
            ) : (
              <motion.div
                key="bulk-form"
                initial={pageMotion.initial}
                animate={pageMotion.animate}
                exit={pageMotion.exit}
                transition={pageMotion.transition}
                style={{ width: "100%" }}
              >
                <Toolbar>
                  <PageTitle>Responder vários alertas</PageTitle>
                  <GhostButton type="button" onClick={goBack}>
                    Voltar
                  </GhostButton>
                </Toolbar>
                <Hint>
                  Informe os códigos dos alertas. Cada cartão mostra o código e a
                  resposta já registrada. A nova resposta é gravada de uma vez,
                  sem as etapas de equipe e ciência.
                </Hint>
                <CodesField
                  value={codesText}
                  onChange={(event) => setCodesText(event.target.value)}
                  placeholder="Ex.: 3CA05, F4D6C"
                  aria-label="Códigos dos alertas"
                />
                <RowActions>
                  <PrimaryButton type="button" onClick={loadAlerts} disabled={loading}>
                    {loading ? "Carregando…" : "Carregar alertas"}
                  </PrimaryButton>
                  <PrimaryButton
                    type="button"
                    onClick={submitAll}
                    disabled={submitting || readyCount === 0}
                  >
                    {submitting ? "Registrando…" : "Registrar respostas"}
                  </PrimaryButton>
                </RowActions>
                {loadError ? <ErrorText>{loadError}</ErrorText> : null}

                <CardList>
                  {items.map((item) => (
                    <AlertCard key={item.code}>
                      <CardHead>
                        <div>
                          <CodeBadge>{item.code}</CodeBadge>
                          {item.alert ? (
                            <MetaLine>
                              {[
                                item.alert.area_name,
                                item.alert.project_title,
                                item.alert.alert_type_description,
                                item.alert.severity_level_description,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "Alerta carregado"}
                            </MetaLine>
                          ) : null}
                        </div>
                        <GhostButton
                          type="button"
                          onClick={() =>
                            setItems((current) =>
                              current.filter((row) => row.code !== item.code)
                            )
                          }
                        >
                          Remover
                        </GhostButton>
                      </CardHead>

                      {item.error ? (
                        <ErrorText>{item.error}</ErrorText>
                      ) : (
                        <>
                          <ResponseBlock>
                            <ResponseLabel>Resposta atual</ResponseLabel>
                            <ResponseValue>
                              {displayText(item.alert.last_ticket?.responses)}
                            </ResponseValue>
                            <ResponseLabel style={{ marginTop: 8 }}>
                              Observação atual
                            </ResponseLabel>
                            <ResponseValue>
                              {displayText(item.alert.last_ticket?.observations)}
                            </ResponseValue>
                            <ResponseLabel style={{ marginTop: 8 }}>
                              Última ação
                            </ResponseLabel>
                            <ResponseValue>
                              {item.alert.last_ticket?.action_display ||
                                item.alert.last_ticket?.action ||
                                "Sem ticket"}
                            </ResponseValue>
                          </ResponseBlock>

                          <FieldLabel htmlFor={`resp-${item.code}`}>Resposta</FieldLabel>
                          <TextArea
                            id={`resp-${item.code}`}
                            value={item.responses}
                            onChange={(event) =>
                              updateItem(item.code, {
                                responses: event.target.value,
                                status: "idle",
                                message: "",
                              })
                            }
                            placeholder="O que foi feito neste alerta"
                          />
                          <FieldLabel htmlFor={`obs-${item.code}`}>Observações</FieldLabel>
                          <TextArea
                            id={`obs-${item.code}`}
                            value={item.observations}
                            onChange={(event) =>
                              updateItem(item.code, {
                                observations: event.target.value,
                                status: "idle",
                                message: "",
                              })
                            }
                            placeholder="Opcional"
                          />
                          {item.message ? (
                            <StatusLine $tone={item.status === "saved" ? "ok" : "error"}>
                              {item.message}
                            </StatusLine>
                          ) : null}
                        </>
                      )}
                    </AlertCard>
                  ))}
                </CardList>
              </motion.div>
            )}
          </AnimatePresence>
        </PageContent>
      </SideContainer>
    </MainContainer>
  );
}
